/**
 * HTTP client used by every API module.
 *
 * - Reads the bearer token from `TokenStore` (localStorage-backed).
 * - On 401, clears the token and dispatches a `piggy:auth-expired` event;
 *   `AuthContext` listens and routes to /login.
 * - Throws `ApiError` for all non-2xx responses so TanStack Query's
 *   error path picks them up uniformly.
 */
const TOKEN_KEY = "piggy.token";
const IMPERSONATE_KEY = "piggy.impersonate_org";

export class TokenStore {
  static get(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }
  static set(token: string): void {
    localStorage.setItem(TOKEN_KEY, token);
  }
  static clear(): void {
    localStorage.removeItem(TOKEN_KEY);
  }
}

/**
 * Per-tab super-admin impersonation override. Stored in sessionStorage
 * (not localStorage) so two browser tabs can act as different orgs at
 * the same time. The HTTP client reads this on every request and sends
 * `X-Impersonate-Org` if set; the backend ignores the header for
 * non-superusers, so a regular user setting it via devtools is harmless.
 */
export class ImpersonateStore {
  static get(): string | null {
    return sessionStorage.getItem(IMPERSONATE_KEY);
  }
  static set(orgId: string): void {
    sessionStorage.setItem(IMPERSONATE_KEY, orgId);
    window.dispatchEvent(new CustomEvent("piggy:impersonate-change"));
  }
  static clear(): void {
    sessionStorage.removeItem(IMPERSONATE_KEY);
    window.dispatchEvent(new CustomEvent("piggy:impersonate-change"));
  }
}

export class ApiError extends Error {
  constructor(public readonly status: number, message: string, public readonly body?: unknown) {
    super(message);
    this.name = "ApiError";
  }
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  /** When true, encode `body` as application/x-www-form-urlencoded (login). */
  form?: boolean;
  /** When true, suppress the auth-expired dispatch on 401 (used by /auth/jwt/login). */
  skipAuthExpiredOn401?: boolean;
  /** Extra headers (e.g. Content-Type override). */
  headers?: Record<string, string>;
  /** Skip Authorization header entirely (used by public branding endpoint). */
  noAuth?: boolean;
}

export async function request<T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(opts.headers ?? {}),
  };

  let body: BodyInit | undefined;
  if (opts.body !== undefined) {
    if (opts.form) {
      const params = new URLSearchParams();
      for (const [k, v] of Object.entries(opts.body as Record<string, string>)) {
        if (v !== undefined && v !== null && v !== "") params.set(k, String(v));
      }
      body = params.toString();
      headers["Content-Type"] = "application/x-www-form-urlencoded";
    } else {
      body = JSON.stringify(opts.body);
      headers["Content-Type"] = "application/json";
    }
  }

  if (!opts.noAuth) {
    const token = TokenStore.get();
    if (token) headers.Authorization = `Bearer ${token}`;
    const impersonate = ImpersonateStore.get();
    if (impersonate) headers["X-Impersonate-Org"] = impersonate;
  }

  const res = await fetch(path, {
    method: opts.method ?? (opts.body !== undefined ? "POST" : "GET"),
    headers,
    body,
  });

  if (res.status === 204) return undefined as T;

  const contentType = res.headers.get("Content-Type") ?? "";
  const payload = contentType.includes("application/json") ? await res.json() : await res.text();

  if (!res.ok) {
    if (res.status === 401 && !opts.skipAuthExpiredOn401) {
      TokenStore.clear();
      window.dispatchEvent(new CustomEvent("piggy:auth-expired"));
    }
    const message = (typeof payload === "object" && payload && "detail" in payload
      ? String((payload as { detail: unknown }).detail)
      : String(payload)) || `${res.status} ${res.statusText}`;
    throw new ApiError(res.status, `${res.status}: ${message}`, payload);
  }

  return payload as T;
}
